import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bug,
  Check,
  CheckCircle2,
  Clock,
  Loader2,
  Monitor,
  Play,
  Wallet,
  X,
} from "lucide-react";
import { Badge, SeverityTag } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";

// The dashboard half of the demo: what a tester sees in Exe → Run while the
// agent works. Presentational only — the timeline in ExeDemo feeds it.

const PHASE_PILL = {
  idle: { tone: "slate", label: "Idle" },
  dispatch: { tone: "amber", label: "Sending to PC" },
  running: { tone: "brand", label: "Running" },
};

const VERDICT_TONE = { PASSED: "emerald", FAILED: "rose", PARTIAL: "amber" };

function StepIcon({ status }) {
  if (status === "ok")
    return (
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
        <Check size={12} strokeWidth={3} />
      </span>
    );
  if (status === "bad")
    return (
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-rose-500 text-white">
        <X size={12} strokeWidth={3} />
      </span>
    );
  return (
    <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-500/15 text-brand-500">
      <Loader2 size={13} className="animate-spin" />
    </span>
  );
}

function Shot({ shot }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        "w-16 shrink-0 overflow-hidden rounded-lg border",
        shot.failed
          ? "border-rose-300 dark:border-rose-500/40"
          : "border-slate-200 dark:border-white/10"
      )}
    >
      <div
        className={cn(
          "flex h-10 flex-col gap-1 p-1.5",
          shot.failed
            ? "bg-rose-50 dark:bg-rose-500/10"
            : "bg-sky-50 dark:bg-sky-500/10"
        )}
      >
        <span
          className={cn(
            "h-1 w-3/5 rounded-full",
            shot.failed ? "bg-rose-400/70" : "bg-sky-400/70"
          )}
        />
        <span
          className={cn(
            "flex-1 rounded",
            shot.failed ? "bg-rose-300/50" : "bg-sky-300/50"
          )}
        />
      </div>
      <span className="block truncate px-1.5 py-0.5 font-mono text-[0.5625rem] text-slate-400">
        {shot.label}
      </span>
    </motion.div>
  );
}

export default function RunConsole({
  scenario,
  phase,
  steps,
  shots,
  suiteState,
  result,
  onRun,
}) {
  const pill = PHASE_PILL[phase] || PHASE_PILL.idle;
  const running = phase === "dispatch" || phase === "running";

  // The list is taller than its box on a long run — keep the newest step in
  // view instead of leaving the visitor watching the first three forever.
  const streamRef = useRef(null);
  useEffect(() => {
    const node = streamRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [steps.length, result]);

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-ink-900 lg:h-[35rem]">
      {/* what is being run, and where. grid-cols-1 (not a bare `grid`) so the
          implicit column can't size to max-content and push rows past the
          card's padding — the device pill and suite labels did exactly that. */}
      <div className="grid grid-cols-1 gap-2.5 border-b border-slate-200 p-3.5 dark:border-white/10">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-bold text-slate-900 dark:text-white">Exe · Run</span>
          {result ? (
            <Badge tone={VERDICT_TONE[result.verdict]}>
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              {result.headline}
            </Badge>
          ) : (
            <Badge tone={pill.tone}>
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full bg-current",
                  running && "animate-pulse"
                )}
              />
              {pill.label}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 p-2 dark:border-white/10 dark:bg-white/5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-brand-200 bg-brand-50 text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
            <Monitor size={15} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold text-slate-800 dark:text-white">
              QA laptop (DESKTOP-VKL2N4P)
            </span>
            <span className="block truncate font-mono text-[0.625rem] text-slate-400">
              {scenario.device}
            </span>
          </span>
          <Badge tone="emerald" className="shrink-0">
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            Online
          </Badge>
        </div>

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2.5 dark:border-white/10 dark:bg-white/5">
          <p className="text-xs font-semibold text-slate-800 dark:text-white">
            {scenario.title}
          </p>
          <p className="mt-0.5 text-[0.6875rem] leading-snug text-slate-500 dark:text-slate-400">
            {scenario.goal}
          </p>
        </div>

        {scenario.suite && (
          <div className="grid grid-cols-1 gap-1.5">
            <div className="flex gap-1">
              {scenario.suite.map((name, i) => (
                <span
                  key={name}
                  className={cn(
                    "h-1.5 flex-1 rounded-full transition-colors",
                    suiteState[i] === "ok" && "bg-emerald-500",
                    suiteState[i] === "bad" && "bg-rose-500",
                    suiteState[i] === "run" && "animate-pulse bg-brand-400",
                    !suiteState[i] && "bg-slate-200 dark:bg-white/10"
                  )}
                />
              ))}
            </div>
            <div className="flex justify-between text-[0.625rem] font-semibold text-slate-400">
              {scenario.suite.map((name) => (
                <span key={name}>{name}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Live stream. Steps, evidence and the bug share ONE scroll region —
          split into separate boxes the steps got squeezed to a single row on a
          long run and the newest one could never be scrolled into view. */}
      <div
        ref={streamRef}
        className="max-h-[24rem] min-h-0 flex-1 overflow-y-auto lg:max-h-none"
      >
        <div className="space-y-1.5 p-3.5">
          {steps.length === 0 && (
            <p className="py-6 text-center text-xs text-slate-400">
              Press <span className="font-semibold text-slate-500">Run</span> — the steps
              stream in here as the agent does them.
            </p>
          )}
          <AnimatePresence initial={false}>
            {steps.map((step, i) => (
              <motion.div
                key={`${step.label}-${i}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className={cn(
                  "flex items-center gap-2.5 rounded-xl border px-2.5 py-2 text-xs",
                  step.status === "bad"
                    ? "border-rose-200 bg-rose-50/70 dark:border-rose-500/25 dark:bg-rose-500/10"
                    : "border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/5"
                )}
              >
                <StepIcon status={step.status} />
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate",
                    step.status === "bad"
                      ? "font-semibold text-rose-600 dark:text-rose-300"
                      : "text-slate-600 dark:text-slate-300"
                  )}
                >
                  {step.label}
                </span>
                {step.ai && (
                  <span className="shrink-0 rounded border border-brand-200 bg-brand-50 px-1.5 text-[0.5625rem] font-bold text-brand-600 dark:border-brand-500/25 dark:bg-brand-500/10 dark:text-brand-300">
                    AI
                  </span>
                )}
                {step.status !== "run" && (
                  <span className="shrink-0 font-mono text-[0.625rem] tabular-nums text-slate-400">
                    {step.ms}
                  </span>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {shots.length > 0 && (
          <div className="px-3.5 pb-3">
            <p className="mb-1.5 text-[0.625rem] font-bold uppercase tracking-wider text-slate-400">
              Evidence collected automatically
            </p>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
              {shots.map((shot) => (
                <Shot key={shot.id} shot={shot} />
              ))}
            </div>
          </div>
        )}

        <AnimatePresence>
          {result?.bug && (
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-3.5 mb-3 rounded-xl border border-rose-200 bg-rose-50/70 p-3 dark:border-rose-500/25 dark:bg-rose-500/10"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="flex items-start gap-1.5 text-xs font-bold leading-snug text-slate-900 dark:text-white">
                  <Bug size={14} className="mt-0.5 shrink-0 text-rose-500" />
                  {result.bug.title}
                </span>
                <SeverityTag severity={result.bug.severity} />
              </div>
              <ul className="mt-2 list-disc space-y-0.5 pl-5 text-[0.6875rem] text-slate-600 dark:text-slate-300">
                {result.bug.repro.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {result.bug.evidence.map((chip) => (
                  <span
                    key={chip}
                    className="rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-[0.625rem] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {result && !result.bug && result.note && (
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-3.5 mb-3 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 text-[0.6875rem] text-slate-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-slate-200"
            >
              <CheckCircle2 size={15} className="mt-px shrink-0 text-emerald-500" />
              {result.note}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* cost meter + the button that starts it all */}
      <div className="flex items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/5">
        <div className="flex items-center gap-3 text-[0.6875rem] text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1.5">
            <Clock size={13} />
            <span className="font-mono tabular-nums text-slate-700 dark:text-slate-200">
              {result?.duration || "—"}
            </span>
          </span>
          <span className="flex items-center gap-1.5">
            <Wallet size={13} />
            <span
              className={cn(
                "font-mono tabular-nums",
                result?.free
                  ? "font-bold text-emerald-600 dark:text-emerald-400"
                  : "text-slate-700 dark:text-slate-200"
              )}
            >
              {result ? `${result.cost} · ${result.tokens}` : "—"}
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={onRun}
          disabled={running}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-brand-600 px-3.5 py-2 text-xs font-bold text-white shadow-soft transition-colors hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Play size={13} />
          {running ? "Running…" : scenario.runLabel}
        </button>
      </div>
    </div>
  );
}
